import React, { useEffect } from 'react';
import {
	SpaceBetween,
	Container,
	Header,
	KeyValuePairs,
	Spinner,
	Box,
} from '@amzn/awsui-components-console';

import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import {
	GetOperationResponse,
	SupportedOperation,
} from '@amzn/snow-data-service-typescript-client';
import { mergeSupportedOperationAndMetadata } from '../ExportOperationToTask/ExportTaskFormGenerationUtils';
import { CombinedOperation } from 'src/@types/serviceOnboardingTypes/operation.interface';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useStore } from 'src/stores/store';
import {
	getOperationDetailsItems,
	renderTaskInputSection,
	renderTaskOutputSection,
} from './OperationDetailsConstants';

interface OperationDetailsProps {
	operationId: string | null;
	onExportToTask?: (operation?: CombinedOperation) => void;
	supportedOperation?: SupportedOperation;
}

const OperationDetails: React.FC<OperationDetailsProps> = ({
	operationId,
	supportedOperation,
}) => {
	const { navigate } = useNavigation();
	const { addNavigationError } = useStore();

	const { isLoading, data, error } = useFetchForSDS<GetOperationResponse>({
		path: operationId ? `/operation/${operationId}` : '',
		method: 'GET',
		url: SDS_APIGATEWAY_ENDPOINT,
	});

	useEffect(() => {
		if (!operationId || !supportedOperation) {
			addNavigationError({
				message: 'Operation details must be accessed from the operations list.',
				type: 'warning',
				timestamp: Date.now(),
			});
			navigate(ROUTES.APPLICATIONS, '/applicationManagement');
		}
	}, [operationId, supportedOperation, addNavigationError, navigate]);

	useEffect(() => {
		if (error) {
			addNavigationError({
				message: `Failed to load operation details: ${error}`,
				type: 'error',
				timestamp: Date.now(),
			});
		}
	}, [error, addNavigationError]);

	if (!operationId || !supportedOperation) {
		return null;
	}

	const operationData = mergeSupportedOperationAndMetadata(
		supportedOperation,
		data?.operationMetadata
	);

	const {
		operationInfoItems,
		requestDetailsItems,
		apiGatewayItems,
		awsAuthItems,
		serviceInfoItems,
		auditInfoItems,
		taskInputItems,
		taskOutputItems,
		requestBodySection,
		responseBodySection,
		successCriteriaSection,
		failureCriteriaSection,
		stageEndpointsMappingSection,
		stageDefaultRegionMappingSection,
	} = operationData
		? getOperationDetailsItems(operationData, operationId)
		: {
			operationInfoItems: [],
			requestDetailsItems: [],
			apiGatewayItems: [],
			awsAuthItems: [],
			serviceInfoItems: [],
			auditInfoItems: [],
			taskInputItems: [],
			taskOutputItems: [],
			requestBodySection: null,
			responseBodySection: null,
			successCriteriaSection: null,
			failureCriteriaSection: null,
			stageEndpointsMappingSection: null,
			stageDefaultRegionMappingSection: null,
		  };

	return isLoading ? (
		<Box textAlign="center" padding="l">
			<Spinner size="large" />
		</Box>
	) : error ? null : (
		<SpaceBetween size="l">
			<Container header={<Header variant="h2">Operation Information</Header>}>
				<KeyValuePairs
					columns={2}
					items={operationInfoItems}
					data-testid="operation-info-items-key-value-pairs"
				/>
			</Container>

			{serviceInfoItems.length > 0 && (
				<Container header={<Header variant="h2">Service Information</Header>}>
					<KeyValuePairs
						columns={2}
						items={serviceInfoItems}
						data-testid="service-info-items-key-value-pairs"
					/>
				</Container>
			)}

			<Container header={<Header variant="h2">Request Details</Header>}>
				<SpaceBetween size="l">
					{requestDetailsItems.length > 0 && (
						<KeyValuePairs
							columns={2}
							items={requestDetailsItems}
							data-testid="request-details-items-key-value-pairs"
						/>
					)}
					{stageEndpointsMappingSection}

					{stageDefaultRegionMappingSection}

					{requestBodySection}

					{renderTaskInputSection(taskInputItems)}

					{apiGatewayItems.length > 0 && (
						<Container
							header={<Header variant="h3">API Gateway Configuration</Header>}
						>
							<KeyValuePairs
								columns={2}
								items={apiGatewayItems}
								data-testid="api-gateway-config-key-value-pairs"
							/>
						</Container>
					)}

					{awsAuthItems.length > 0 && (
						<Container
							header={<Header variant="h3">AWS Auth Parameters</Header>}
						>
							<KeyValuePairs
								columns={2}
								items={awsAuthItems}
								data-testid="aws-auth-params-key-value-pairs"
							/>
						</Container>
					)}
				</SpaceBetween>
			</Container>

			<Container header={<Header variant="h2">Response Details</Header>}>
				<SpaceBetween size="l">
					{responseBodySection}
					{renderTaskOutputSection(taskOutputItems)}
					{successCriteriaSection}
					{failureCriteriaSection}
				</SpaceBetween>
			</Container>

			<Container header={<Header variant="h2">Audit Information</Header>}>
				<KeyValuePairs
					columns={2}
					items={auditInfoItems}
					data-testid="audit-info-items-key-value-pairs"
				/>
			</Container>
		</SpaceBetween>
	);
};

export default OperationDetails;
